export type TaskAlertBanner =
  | 'lowTasks'
  | 'outOfTasks'
  | 'aboutToEnterMeteredBilling'
  | 'enteredMeteredBilling';

type TaskAlertBanners = Record<
  TaskAlertBanner,
  {
    // Local storage key for dismissing the banner
    dismissalKey: string;
    // The type of banner depending on the task usage and metered billing status
    banner: TaskAlertBanner;
  }
>;

export const TASK_ALERT_BANNERS: TaskAlertBanners = {
  lowTasks: {
    dismissalKey: 'lowTasksBannerDismissed',
    banner: 'lowTasks',
  },
  outOfTasks: {
    dismissalKey: 'outOfTasksBannerDismissed',
    banner: 'outOfTasks',
  },
  aboutToEnterMeteredBilling: {
    dismissalKey: 'aboutToEnterMeteredBillingBannerDismissed',
    banner: 'aboutToEnterMeteredBilling',
  },
  enteredMeteredBilling: {
    dismissalKey: 'enteredMeteredBillingBannerDismissed',
    banner: 'enteredMeteredBilling',
  },
} as const;

/**
 * An account is in metered billing when the number of consumed tasks
 * is greater than or equal the number of base tasks available for a particular plan.
 */
const accountIsInMeteredBilling = ({
  accountConsumedTasks,
  includedTasksPerMonth,
}: {
  accountConsumedTasks: number;
  includedTasksPerMonth: number;
}) => {
  return accountConsumedTasks >= includedTasksPerMonth;
};

const accountHasReachedPercentageOfTaskLimit = ({
  percentage,
  taskBufferOverride,
  maxTasks,
  consumedTasks,
}: {
  // The percentage of task limit used to determine if the account has reached a threshold
  percentage: number;
  // The total number of free tasks allowed in an usage period
  maxTasks: number;
  // The total number of tasks used in an usage period
  consumedTasks: number;
  // The buffer multiplier for task usage limit in an usage period (legacy)
  taskBufferOverride?: number | null;
}) => {
  return consumedTasks / (maxTasks * (taskBufferOverride || 1)) >= percentage;
};

/**
 * For metered plans, calculates the number of consumed base tasks
 * based on the total number of used tasks compared to the number
 * of base tasks available for a particular plan.
 */
const calculateConsumedBaseTasks = ({
  accountConsumedTasks,
  includedTasksPerMonth,
  isInMeteredBilling,
}: {
  accountConsumedTasks: number;
  includedTasksPerMonth?: number;
  isInMeteredBilling: boolean;
}) => {
  const hasConsumedTasks = includedTasksPerMonth && accountConsumedTasks > 0;
  if (!hasConsumedTasks) return 0;

  return isInMeteredBilling ? includedTasksPerMonth : accountConsumedTasks;
};

const getTaskAlertBannerEligibilityForMeteredBilling = ({
  accountConsumedTasks,
  includedTasksPerMonth,
}: {
  accountConsumedTasks: number;
  includedTasksPerMonth: number;
}) => {
  const isInMeteredBilling = accountIsInMeteredBilling({
    accountConsumedTasks,
    includedTasksPerMonth,
  });

  const isAboutToEnterMeteredBilling = accountHasReachedPercentageOfTaskLimit({
    percentage: 0.8,
    maxTasks: includedTasksPerMonth,
    consumedTasks: calculateConsumedBaseTasks({
      accountConsumedTasks,
      includedTasksPerMonth,
      isInMeteredBilling,
    }),
  });

  if (isAboutToEnterMeteredBilling && !isInMeteredBilling) {
    return TASK_ALERT_BANNERS.aboutToEnterMeteredBilling.banner;
  }

  if (isInMeteredBilling) {
    return TASK_ALERT_BANNERS.enteredMeteredBilling.banner;
  }

  return null;
};

const getTaskAlertBannerEligibilityForLegacyBilling = ({
  taskBufferOverride,
  accountConsumedTasks,
  includedTasksPerMonth,
}: {
  taskBufferOverride?: number | null;
  accountConsumedTasks: number;
  includedTasksPerMonth: number;
}) => {
  const hasReachedLowTaskThreshold = accountHasReachedPercentageOfTaskLimit({
    percentage: 0.8,
    taskBufferOverride: taskBufferOverride || 1,
    maxTasks: includedTasksPerMonth,
    consumedTasks: accountConsumedTasks,
  });

  const hasReached100PercentTaskLimit = accountHasReachedPercentageOfTaskLimit({
    percentage: 1,
    maxTasks: includedTasksPerMonth,
    consumedTasks: accountConsumedTasks,
    taskBufferOverride: taskBufferOverride || 1,
  });

  if (hasReachedLowTaskThreshold && !hasReached100PercentTaskLimit) {
    return TASK_ALERT_BANNERS.lowTasks.banner;
  }

  if (hasReached100PercentTaskLimit) {
    return TASK_ALERT_BANNERS.outOfTasks.banner;
  }

  return null;
};

export const getTaskAlertBannerEligibility = ({
  accountHasMeteredBilling,
  accountConsumedTasks,
  includedTasksPerMonth,
  taskBufferOverride,
}: {
  accountHasMeteredBilling: boolean;
  accountConsumedTasks: number;
  includedTasksPerMonth: number;
  taskBufferOverride: number | null;
}) => {
  if (accountHasMeteredBilling) {
    return getTaskAlertBannerEligibilityForMeteredBilling({
      accountConsumedTasks,
      includedTasksPerMonth,
    });
  }

  return getTaskAlertBannerEligibilityForLegacyBilling({
    accountConsumedTasks,
    includedTasksPerMonth,
    taskBufferOverride,
  });
};
